<template>
    <form @change="isFormDirty" @submit.prevent="handleSubmit" class="form-container" data-cy="form-container">
        <p class="text-center text-default-red p-2" v-if="validation.message">
            {{ validation.message }}
        </p>
        <div class="form-section">
            <div class="section-body" data-cy="section-general">
                <div
                    class="input-container w-full px-4 mb-2"
                    data-cy="user-settings-company-id"
                    v-if="is_association_section"
                >
                    <ui-label>
                        {{ $t('user.forms.main.company_id') }}
                    </ui-label>
                    <div v-if="ifExistingUser" class="subText pt-1 pl-1">{{ user.company_name }}</div>
                    <ui-select
                        v-else
                        :class="{ 'form-error': v$.user.company_id.$error }"
                        v-model="v$.user.company_id.$model"
                        :options="companies"
                        :searchable="true"
                        @updatedValue="updateCompany"
                    />
                    <div
                        data-cy="field-company_id-error"
                        :class="{ 'text-default-red': v$.user.company_id.$error }"
                        v-if="v$.user.company_id.required.$invalid && v$.user.company_id.$error"
                    >
                        {{ $t('validation.required') }}
                    </div>
                    <validation-errors :errors="validation.errors.company_id" v-if="validation.errors" />
                </div>
                <div
                    class="input-container w-full px-4 mb-2"
                    data-cy="user-settings-company-id"
                    v-if="is_admin_section"
                >
                    <ui-label>
                        {{ $t('user.forms.main.company_id') }}
                    </ui-label>
                    <div v-if="ifExistingUser" class="subText pt-1 pl-1">{{ user.company_name }}</div>
                    <ui-select
                        v-else
                        :class="{ 'form-error': v$.user.company_id.$error }"
                        v-model="v$.user.company_id.$model"
                        :options="associations_companies"
                        :searchable="true"
                        @updatedValue="updateCompany"
                    />
                    <div
                        data-cy="field-company_id-error"
                        :class="{ 'text-default-red': v$.user.company_id.$error }"
                        v-if="v$.user.company_id.required.$invalid && v$.user.company_id.$error"
                    >
                        {{ $t('validation.required') }}
                    </div>
                    <validation-errors :errors="validation.errors.company_id" v-if="validation.errors" />
                </div>
                <div class="input-container w-full px-4 mb-2" data-cy="user-name-field">
                    <ui-label>{{ $t('user.forms.main.name') }}</ui-label>
                    <ui-input v-model="v$.user.name.$model" :class="{ 'form-error': v$.user.name.$error }" />
                    <div
                        data-cy="field-name-error"
                        :class="{ 'text-default-red': v$.user.name.$error }"
                        v-if="v$.user.name.required.$invalid && v$.user.name.$error"
                    >
                        {{ $t('validation.required') }}
                    </div>
                </div>
                <div class="input-container w-full px-4 mb-2" data-cy="user-email-field">
                    <span class="flex justify-between">
                        <ui-label>{{ $t('user.forms.main.email') }}</ui-label>
                        <a-tooltip
                            v-if="isSiteAdmin && ifExistingUser"
                            placement="right"
                            :title="$t('user.forms.main.toggle')"
                        >
                            <EditOutlined
                                data-cy="drawer-toggle-email"
                                @click="toggleEmail"
                                class="hover:text-blue-400 mr-1"
                                :disabled="processing"
                            />
                        </a-tooltip>
                    </span>
                    <ui-input
                        v-model="v$.user.email.$model"
                        :class="{ 'form-error': v$.user.email.$error }"
                        :disabled="disable_email"
                    />
                    <div
                        data-cy="field-email-error"
                        :class="{ 'text-default-red': v$.user.email.$error }"
                        v-if="v$.user.email.required.$invalid && v$.user.email.$error"
                    >
                        {{ $t('validation.required') }}
                    </div>
                    <div
                        data-cy="field-email-format-error"
                        :class="{ 'text-default-red': v$.user.email.$error }"
                        v-if="v$.user.email.email.$invalid && v$.user.email.$error && !v$.user.email.required.$invalid"
                    >
                        {{ $t('validation.email', { attribute: $t('user.forms.main.email') }) }}
                    </div>
                </div>
                <div class="input-container w-1/2 px-4 mb-2" data-cy="user-settings-timezone">
                    <ui-label>
                        {{ $t('user.forms.main.timezone') }}
                    </ui-label>
                    <ui-select
                        :class="{ 'form-error': v$.user.timezone.$error }"
                        v-model="v$.user.timezone.$model"
                        :options="timezones"
                        :searchable="true"
                    />
                    <div
                        data-cy="field-timezone-error"
                        :class="{ 'text-default-red': v$.user.timezone.$error }"
                        v-if="v$.user.timezone.required.$invalid && v$.user.timezone.$error"
                    >
                        {{ $t('validation.required') }}
                    </div>
                    <validation-errors :errors="validation.errors.timezone" v-if="validation.errors" />
                </div>
                <div class="input-container w-1/2 px-4 mb-2" data-cy="user-settings-locale">
                    <ui-label>
                        {{ $t('user.forms.main.locale') }}
                    </ui-label>
                    <ui-select
                        :class="{ 'form-error': v$.user.locale.$error }"
                        v-model="v$.user.locale.$model"
                        :options="locales"
                        :searchable="true"
                    />
                    <div
                        data-cy="field-locale-error"
                        :class="{ 'text-default-red': v$.user.locale.$error }"
                        v-if="v$.user.locale.required.$invalid && v$.user.locale.$error"
                    >
                        {{ $t('validation.required') }}
                    </div>
                    <validation-errors :errors="validation.errors.locale" v-if="validation.errors" />
                </div>
                <div class="input-container w-1/2 px-4 mb-2" data-cy="user-settings-expiry_date">
                    <ui-label>
                        {{ $t('user.forms.main.expiry_date') }}
                    </ui-label>
                    <ui-date-picker
                        :disabled="this.user.default_expiry_date == null && !ifExistingUser"
                        v-model="v$.user.expiry_date.$model"
                        :class="{ 'form-error': v$.user.expiry_date.$error }"
                        :disableDateFunction="disableDate"
                    />
                    <div
                        data-cy="field-expiry_date-error"
                        :class="{ 'text-default-red': v$.user.expiry_date.$error }"
                        v-if="v$.user.expiry_date.required.$invalid && v$.user.expiry_date.$error"
                    >
                        {{ $t('validation.required') }}
                    </div>
                    <validation-errors :errors="validation.errors.expiry_date" v-if="validation.errors" />
                </div>
                <div class="input-container w-full px-4 mb-2" data-cy="user-settings-signature">
                    <ui-label>
                        {{ $t('user.forms.main.user_signature') }}
                        <a-tooltip placement="right" :title="$t('globals.buttons.delete')">
                            <DeleteOutlined v-if="user.user_signature" class="text-default-red" @click="handleRemove" />
                        </a-tooltip>
                    </ui-label>
                    <a-upload
                        class="w-1/2 avatar-uploader"
                        list-type="picture-card"
                        :show-upload-list="false"
                        :before-upload="beforeUpload"
                    >
                        <img v-if="user.user_signature" :src="user.user_signature" alt="avatar" />
                        <div v-else>
                            <LoadingOutlined v-if="loading" />
                            <PlusOutlined v-else />
                            <div class="ant-upload-text">{{ $t('user.forms.main.upload') }}</div>
                        </div>
                    </a-upload>
                    <validation-errors :errors="validation.errors.user_signature" v-if="validation.errors" />
                </div>
            </div>
        </div>
        <div
            v-if="isEpicVaultEnabled && user.can_edit_modules"
            class="form-section"
            data-cy="user-settings-landing-page"
        >
            <div class="section-body">
                <div v-if="user.can_edit_modules" class="w-full px-4">
                    <ui-label>
                        {{ $t('user.forms.main.modules_section') }}
                    </ui-label>
                    <div v-if="user.can_edit_modules" class="h6">
                        <div data-cy="user-settings-is-module-claimsvault-enabled">
                            <ui-checkbox
                                v-model="user.is_module_claimsvault_enabled"
                                :disabled="canEnableAnalytics || isCurrentUser"
                                @updateFlag="onVaultCheckboxChange('claims')"
                            >
                                {{ $t('user.forms.main.is_module_claimsvault_enabled') }}
                            </ui-checkbox>
                        </div>
                    </div>
                    <div v-if="user.can_edit_modules" class="h6">
                        <div data-cy="user-settings-is-module-covervault-enabled">
                            <ui-checkbox
                                v-model="user.is_module_covervault_enabled"
                                :disabled="canEnableAnalytics || isCurrentUser"
                                @updateFlag="onVaultCheckboxChange('cover')"
                            >
                                {{ $t('user.forms.main.is_module_covervault_enabled') }}
                            </ui-checkbox>
                        </div>
                    </div>
                </div>
                <div class="input-container w-1/2 px-4 mb-2" data-cy="user-settings-landing-page">
                    <ui-label>
                        {{ $t('user.forms.main.default_landing_page') }}
                    </ui-label>
                    <ui-select
                        :class="{ 'form-error': v$.user.default_landing_page.$error }"
                        v-model="v$.user.default_landing_page.$model"
                        :options="default_landing_page"
                    />
                    <div
                        data-cy="field-default-landing-page-error"
                        :class="{ 'text-default-red': v$.user.default_landing_page.$error }"
                        v-if="v$.user.default_landing_page.requiredIf.$invalid && v$.user.default_landing_page.$error"
                    >
                        {{ $t('validation.required') }}
                    </div>
                </div>
                <div
                    v-if="isEpicVaultEnabled"
                    data-cy="user-settings-settings-enable-advanced-claims-analytics"
                    class="h-6 px-4"
                >
                    <ui-checkbox
                        v-model="user.enable_advanced_claims_analytics"
                        :disabled="canEnableAnalytics || !user.is_module_claimsvault_enabled"
                    >
                        {{ $t('user.forms.main.enable_advanced_claims_analytics') }}
                    </ui-checkbox>
                </div>
                <div
                    v-if="isEpicVaultEnabled"
                    data-cy="user-settings-settings-enable-advanced-covers-analytics"
                    class="h-6 px-4 mb-2"
                >
                    <ui-checkbox
                        v-model="user.enable_advanced_cover_analytics"
                        :disabled="canEnableAnalytics || !user.is_module_covervault_enabled"
                    >
                        {{ $t('user.forms.main.enable_advanced_cover_analytics') }}
                    </ui-checkbox>
                </div>
            </div>
        </div>
        <div class="form-section" data-cy="section-roles">
            <div class="section-body">
                <div class="input-container w-1/2 px-4 mb-2" data-cy="user-settings-roles">
                    <ui-label>
                        {{ $t('user.forms.main.user_role') }}
                    </ui-label>
                    <ui-checkbox-group
                        :options="
                            roles.filter((role) => {
                                return ['admin', 'association_admin', 'site_admin', 'user'].includes(role.value);
                            })
                        "
                        v-model="user.roles"
                        @update-roles="updateUserRoles"
                    />
                    <ui-label>
                        {{ $t('user.forms.main.system_connection') }}
                    </ui-label>
                    <ui-checkbox-group
                        :options="
                            roles.filter((role) => {
                                return ['api_user', 'data_sender'].includes(role.value);
                            })
                        "
                        v-model="user.roles"
                        @update-roles="updateSystemRoles"
                    >
                    </ui-checkbox-group>
                    <ui-label v-if="user.is_module_covervault_enabled">
                        {{ $t('user.forms.main.cover_roles') }}
                    </ui-label>
                    <ui-checkbox-group
                        v-if="user.is_module_covervault_enabled && isCiaEnabled"
                        :options="
                            roles.filter((role) => {
                                return ['authorizer_user'].includes(role.value);
                            })
                        "
                        v-model="user.roles"
                        @update-roles="updateCoverRoles"
                    >
                    </ui-checkbox-group>
                </div>
            </div>
        </div>

        <div class="form-section mb-12" data-cy="section-flags">
            <div class="section-body">
                <div class="input-container checkbox-container w-full px-4 mb-2">
                    <ui-checkbox :disabled="!allow_active" data-cy="field-is-active" v-model="user.is_active">
                        {{ $t('user.forms.main.active') }}
                    </ui-checkbox>
                </div>
                <div class="input-container checkbox-container w-full px-4 mb-2" v-if="user.is_association_tfa_enabled">
                    <ui-checkbox
                        data-cy="field-is-tfa-enabled"
                        v-model="user.is_association_tfa_enabled"
                        :disabled="true"
                    >
                        {{ $t('user.forms.main.is_association_tfa_enabled') }}
                    </ui-checkbox>
                </div>
                <div
                    class="input-container checkbox-container w-full px-4 mb-2"
                    v-else-if="user.is_company_tfa_enabled"
                >
                    <ui-checkbox data-cy="field-is-tfa-enabled" v-model="user.is_company_tfa_enabled" :disabled="true">
                        {{ $t('user.forms.main.is_company_tfa_enabled') }}
                    </ui-checkbox>
                </div>
                <div class="input-container checkbox-container w-full px-4 mb-2" v-else>
                    <ui-checkbox data-cy="field-is-tfa-enabled" v-model="user.is_tfa_enabled">
                        {{ $t('user.forms.main.is_tfa_enabled') }}
                    </ui-checkbox>
                </div>
                <div class="input-container checkbox-container w-full px-4 mb-2">
                    <ui-checkbox
                        v-if="user.roles.includes('api_user') || user.roles.includes('data_sender')"
                        data-cy="field-is-append-api-user-id-to-cover-link-id"
                        v-model="user.is_append_api_user_id_to_cover_link_id"
                    >
                        {{ $t('user.forms.main.is_append_api_user_id_to_cover_link_id') }}
                    </ui-checkbox>
                </div>
                <div class="input-container checkbox-container w-full px-4 mb-2" v-if="user.roles.includes('admin')">
                    <ui-checkbox v-model="user.is_data_provider_email" :disabled="!disableFlag">
                        {{ $t('user.forms.main.is_data_provider_email') }}
                    </ui-checkbox>
                </div>

                <div class="input-container checkbox-container w-2/3 px-4 mb-2" v-if="allow_multi_association_access">
                    <ui-checkbox data-cy="field-is-multi-association-access" v-model="user.is_multi_association_access">
                        {{ $t('user.forms.main.is_multi_association_access') }}
                    </ui-checkbox>
                </div>
                <div
                    class="input-container mb-2 mt-6 w-full px-4"
                    data-cy="field-access-association-ids"
                    v-if="allow_multi_association_access && user.is_multi_association_access"
                >
                    <ui-label>{{ $t('user.forms.main.access_association_ids') }}</ui-label>
                    <ui-select-multiple
                        v-model="v$.user.access_association_ids.$model"
                        :options="multi_association_access"
                        :class="{ 'form-error': v$.user.access_association_ids.$error }"
                        :searchable="true"
                        :allowClear="true"
                    />
                </div>
                <div
                    class="input-container mb-10 mt-6 w-full px-4"
                    data-cy="field-access-association-ids"
                    v-if="allow_multi_association_access && user.is_multi_association_access"
                >
                    <ui-label>{{ $t('user.forms.main.access_company_ids') }}</ui-label>
                    <ui-select-multiple
                        v-model="v$.user.access_company_ids.$model"
                        :options="multiCompanyList"
                        :class="{ 'form-error': v$.user.access_company_ids.$error }"
                        :searchable="true"
                        :allowClear="true"
                    />
                </div>
            </div>
        </div>

        <div class="drawer-footer">
            <slot name="actions"></slot>

            <a-button data-cy="drawer-cancel-button" @click="onDrawerClose" class="ant-btn mr-2" :disabled="processing">
                {{ $t('user.forms.main.buttons.cancel') }}
            </a-button>
            <ui-submit-button data-cy="drawer-save-btn" :disabled="processing">
                <span v-if="!processing">{{ $t('user.forms.main.buttons.save') }}</span>
                <span v-else>{{ $t('user.forms.main.buttons.saving') }}</span>
            </ui-submit-button>
        </div>
    </form>
</template>

<script>
import UiCheckbox from '@/components/ui/Checkbox.vue';
import UiCheckboxGroup from '@/components/ui/CheckboxGroup.vue';
import UiDatePicker from '@/components/ui/DatePicker.vue';
import UiInput from '@/components/ui/Input.vue';
import UiLabel from '@/components/ui/Label.vue';
import UiSelect from '@/components/ui/Select.vue';
import UiSelectMultiple from '@/components/ui/SelectMultiple.vue';
import UiSubmitButton from '@/components/ui/SubmitButton.vue';
import ValidationErrors from '@/components/ui/ValidationErrors.vue';

import { DeleteOutlined, EditOutlined, LoadingOutlined, PlusOutlined } from '@ant-design/icons-vue';

import { useVuelidate } from '@vuelidate/core';
import { required, email, requiredIf } from '@vuelidate/validators';

import User from '@/models/user.js';

export default {
    setup() {
        return { v$: useVuelidate() };
    },
    components: {
        UiCheckbox,
        UiCheckboxGroup,
        UiDatePicker,
        UiInput,
        UiLabel,
        UiSelect,
        UiSelectMultiple,
        UiSubmitButton,
        ValidationErrors,
        DeleteOutlined,
        EditOutlined,
        LoadingOutlined,
        PlusOutlined,
    },
    validations: {
        user: {
            company_id: { required },
            name: { required },
            email: { required, email },
            timezone: { required },
            locale: { required },
            expiry_date: { required },
            is_active: {},
            default_landing_page: {
                requiredIf: requiredIf(function () {
                    if (this.user.can_edit_modules && this.isEpicVaultEnabled) {
                        return true;
                    } else {
                        return false;
                    }
                }),
            },
            is_append_api_user_id_to_cover_link_id: {},
            is_multi_association_access: {},
            access_association_ids: {},
            access_company_ids: {},
            is_tfa_enabled: {},
            enable_advanced_claims_analytics: {},
            enable_advanced_cover_analytics: {},
            is_module_claimsvault_enabled: {
                epicModules() {
                    return this.epicModules();
                },
            },
            is_module_covervault_enabled: {
                epicModules() {
                    return this.epicModules();
                },
            },
        },
    },
    data() {
        return {
            locales: [],
            timezones: [],
            roles: [],
            companies: [],
            loading: false,
            image_file: {},
            image_updated: false,
            default_landing_page: [
                {
                    value: 'claimsvault_dashboard',
                    text: 'ClaimsVAULT Dashboard',
                },
                {
                    value: 'claimsvault',
                    text: 'ClaimsVAULT',
                },
                {
                    value: 'covervault_dashboard',
                    text: 'CoverVAULT Dashboard',
                },
                {
                    value: 'covervault',
                    text: 'CoverVAULT',
                },
            ],
            associations_companies: [],
            allow_multi_association_access: false,
            multi_association_access: [],
            multi_company_access: [],
            disable_email: false,
            allow_active: false,
        };
    },
    computed: {
        ifExistingUser() {
            if (!(this.user.id === '' || this.user.id === undefined || this.user.id === null)) {
                this.disable_email = true;
                return true;
            }
        },
        isCompanyAdmin() {
            return User.can('manage-company');
        },
        isAssociationAdmin() {
            return User.can('manage-association');
        },
        isSiteAdmin() {
            return User.can('manage-admin');
        },
        isCurrentUser() {
            return User.isCurrentUser(this.user.id);
        },
        canEnableAnalytics() {
            if (User.can('manage-admin')) {
                return false;
            } else if (User.can('manage-association')) {
                return this.user.roles.some((role) => ['association_admin', 'site_admin'].includes(role));
            } else if (User.can('manage-company')) {
                return this.user.roles.some((role) => ['admin', 'association_admin', 'site_admin'].includes(role));
            }
        },
        multiCompanyList() {
            let list = [];
            let available_association_ids = [];

            if (this.user.access_association_ids.length == 0) {
                this.user.access_company_ids = [];
            } else {
                let accepted_company_ids = [];
                let used_association_ids = [];

                // validate selected company ids first
                this.user.access_company_ids.forEach((company_id) => {
                    this.multi_company_access.forEach((record) => {
                        let var_split = record.value.split('_');

                        if (
                            var_split[1] == company_id &&
                            _.indexOf(this.user.access_association_ids, var_split[0]) >= 0
                        ) {
                            accepted_company_ids.push(company_id);
                            used_association_ids.push(var_split[0]);
                        }
                    });
                });

                this.user.access_company_ids = accepted_company_ids;

                this.user.access_association_ids.forEach((association_id) => {
                    if (_.indexOf(used_association_ids, association_id) < 0) {
                        available_association_ids.push(association_id);
                    }
                });

                // formulate the available company list next
                this.multi_company_access.forEach((record) => {
                    let var_split = record.value.split('_');
                    let is_disabled = false;
                    let should_be_added = false;

                    this.user.access_company_ids.forEach((company_id) => {
                        if (var_split[1] == company_id) {
                            should_be_added = true;
                        }
                    });

                    available_association_ids.forEach((association_id) => {
                        if (var_split[0] == association_id) {
                            should_be_added = true;
                        }
                    });

                    if (should_be_added) {
                        list.push({
                            text: record.text,
                            value: var_split[1],
                            disabled: is_disabled,
                        });
                    }
                });
            }

            return list;
        },
        isEpicVaultEnabled() {
            return this.$store.getters['system/isEpicvaultEnabled'];
        },
        isCiaEnabled() {
            return this.$store.state.user.modules.cia;
        },

        disableFlag() {
            return this.isSiteAdmin || this.isAssociationAdmin;
        },
    },
    props: {
        user: Object,
        processing: Boolean,
        validation: Object,
        is_admin_section: {
            type: Boolean,
            default: false,
        },
        is_association_section: {
            type: Boolean,
            default: false,
        },
    },
    mounted() {
        this.fetch();
    },
    methods: {
        epicModules() {
            if (this.isEpicVaultEnabled) {
                if (!this.user.is_module_claimsvault_enabled && !this.user.is_module_covervault_enabled) {
                    return false;
                }
            }

            return true;
        },
        isFormDirty() {
            this.$emit('is-form-dirty', this.v$.$anyDirty);
        },
        fetch() {
            let urls = [
                this.$http.get('/api/listmanager/category/locale'),
                this.$http.get('/api/listmanager/category/timezone'),
                this.$http.get('/api/roles/list'),
            ];

            if (this.is_admin_section) {
                urls.push(this.$http.get('/api/admin/list-active-association-companies'));
            }

            if (this.is_association_section) {
                urls.push(
                    this.$http.get('/api/admin/list-all-active-companies', {
                        params: {
                            association_id: this.$store.getters['user/currentAssociationId'],
                        },
                    }),
                );
            }

            this.$http.all(urls).then(
                this.$http.spread((locales, timezones, roles, companies) => {
                    this.locales = [];
                    this.timezones = [];
                    this.roles = roles.data.roles;
                    this.companies = [];
                    this.associations_companies = [];

                    locales.data.data.forEach((record) => {
                        this.locales.push({
                            text: record.item,
                            value: record.value,
                        });
                    });

                    timezones.data.data.forEach((record) => {
                        this.timezones.push({
                            text: record.item,
                            value: record.value,
                        });
                    });

                    if (this.is_association_section) {
                        companies.data.data.forEach((record) => {
                            this.companies.push({
                                text: record.company_name,
                                value: record.company_id,
                            });
                        });
                    }

                    if (this.is_admin_section) {
                        companies.data.data.forEach((record) => {
                            this.associations_companies.push({
                                text: record.association_code + ' - ' + record.company_name,
                                value: record.company_id,
                            });
                        });
                    }
                }),
            );

            this.updateMultiAssociationList();
        },
        disableDate(current) {
            var today = new this.$dayjs();
            return current.isBefore(today);
        },
        handleRemove(event) {
            if (this.user.user_signature != null && this.user.user_signature != '') {
                this.image_updated = true;
                this.user.user_signature = null;
            }
        },
        beforeUpload(file) {
            this.loading = true;

            this.image_file = file;
            this.image_updated = true;
            const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';

            if (!isJpgOrPng) {
                this.$notification.error({
                    message: this.$t('validation.mimes', { attribute: 'Logo', values: 'JPEG|PNG' }),
                });
            }

            const isLtKB = file.size / 1024 / 1024 < 2;
            if (!isLtKB) {
                this.$notification.error({
                    message: this.$t('validation.lt.file', { attribute: 'Logo', value: 2048 }),
                });
            }

            if (isJpgOrPng && isLtKB) {
                this.user.user_signature = URL.createObjectURL(file);
            }

            this.loading = false;

            return false;
        },
        async handleSubmit() {
            if (!(await this.v$.$validate())) return false;

            let user = {
                ...this.user,
            };

            const fm_data = new FormData();

            Object.keys(user).forEach(function (key_value, index) {
                if (user[key_value] === true) {
                    user[key_value] = 1;
                } else if (user[key_value] === false && user[key_value] !== '') {
                    user[key_value] = 0;
                }
            });

            for (var key in user) {
                if (key != 'user_signature' && key != 'can_edit_modules' && user[key] != null) {
                    fm_data.append(key, user[key]);
                }
            }

            if (this.image_updated) {
                if (user.user_signature != null && user.user_signature != '') {
                    fm_data.set('user_signature', this.image_file);
                } else {
                    fm_data.set('remove_logo', true);
                }
            }

            if (user.expiry_date !== null && user.expiry_date.isValid()) {
                fm_data.set('expiry_date', user.expiry_date.format('YYYY-MM-DD'));
            } else {
                fm_data.set('expiry_date', null);
            }

            fm_data.set('access_association_ids', _.join(user.access_association_ids, ','));
            fm_data.set('access_company_ids', _.join(user.access_company_ids, ','));

            this.image_updated = false;
            this.v$.$reset();

            this.$emit('formSubmit', fm_data);
        },
        onDrawerClose() {
            this.$emit('onDrawerClose');
        },
        updateUserRoles(checked_roles) {
            const all_roles = [
                ...this.user.roles.filter((role) => {
                    return !['user', 'admin', 'association_admin', 'site_admin'].includes(role);
                }),
            ];
            checked_roles.forEach((role) => {
                if (!all_roles.includes(role)) {
                    all_roles.push(role);
                }
            });

            this.$emit('updateRoles', all_roles);
        },
        updateSystemRoles(checked_roles) {
            const all_roles = [
                ...this.user.roles.filter((role) => {
                    return !['api_user', 'data_sender'].includes(role);
                }),
            ];
            checked_roles.forEach((role) => {
                if (!all_roles.includes(role)) {
                    all_roles.push(role);
                }
            });
            this.$emit('updateRoles', all_roles);
        },
        updateCoverRoles(checked_roles) {
            const all_roles = [
                ...this.user.roles.filter((role) => {
                    return !['authorizer_user'].includes(role);
                }),
            ];
            checked_roles.forEach((role) => {
                if (!all_roles.includes(role)) {
                    all_roles.push(role);
                }
            });
            this.$emit('updateRoles', all_roles);
        },
        updateCompany() {
            this.updateMultiAssociationList();

            if (!this.ifExistingUser) {
                this.$emit('updateCompany', this.user.company_id);
            }
        },
        toggleEmail() {
            this.disable_email = !this.disable_email;
        },
        updateMultiAssociationList() {
            if (this.user.company_id !== undefined && this.user.company_id !== null && this.user.company_id !== '') {
                this.multi_association_access = [];

                this.$http
                    .get('/api/companies/' + this.user.company_id + '/if-multi-association')
                    .then((response) => {
                        this.allow_multi_association_access = response.data.is_multi_association_link;

                        if (this.allow_multi_association_access) {
                            response.data.association_list.forEach((record) => {
                                this.multi_association_access.push({
                                    text: record.name,
                                    value: record.id,
                                });

                                record.companies.forEach((company) => {
                                    this.multi_company_access.push({
                                        text: company.name,
                                        value: record.id + '_' + company.id,
                                    });
                                });
                            });
                        }
                    })
                    .catch((error) => {
                        if (error.response.status == 403) {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.403_mod'),
                            });
                        } else {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVMAA01' }),
                            });
                        }
                    });
            }
        },
        onVaultCheckboxChange(value) {
            if (value == 'claims' && !this.user.is_module_claimsvault_enabled) {
                this.user.enable_advanced_claims_analytics = false;
            } else if (!this.user.is_module_covervault_enabled) {
                this.user.enable_advanced_cover_analytics = false;
            }
        },
    },
};
</script>
